import { useEffect, useState } from 'react';
import { CalendarDays, ExternalLink, Send } from 'lucide-react';
import { Modal } from '../../components/Modal/Modal';
import { useToast } from '../../components/Toast';
import {
  STAENDE, STAND_INFO, VERSAND_INFO, datumZeit, eventZuordnen, ladeEventAuswahl, planungSpeichern, zeitraumText,
  type EventAuswahl, type PlanungStand, type PlanungsMail,
} from '../../lib/planung';
import './Dialoge.css';

type Props = { mail: PlanungsMail; offen: boolean; onClose: () => void; onGespeichert: () => void };

const QUELLE_TEXT: Record<string, string> = {
  event_manager: 'aus dem Event Manager',
  assistent: 'aus dem Assistenten',
  name: 'aus dem Namen der Mail',
};

/** Stand eines Events im Event Manager, solange es noch nicht freigegeben ist. */
const EM_STATUS: Record<string, string> = {
  eingereicht: 'eingereicht',
  in_pruefung: 'in Prüfung',
  rueckfrage: 'Rückfrage',
};

/** ISO-Zeitstempel → Wert für <input type="datetime-local"> (Ortszeit). */
function alsEingabe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const z = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}T${z(d.getHours())}:${z(d.getMinutes())}`;
}

/**
 * Termine und Stand einer Mail: geplanter Versand (solange in Brevo nichts
 * eingeplant ist), Event (auch die Zuordnung zu einem Event im Event Manager),
 * Bearbeitungsstand, „Feedback bis", interne Notiz.
 * Leere Felder heißen: Das Mailtool leitet den Wert selbst ab (Event Manager,
 * Mailname) — eingetragene Werte gehen vor.
 */
export function PlanungBearbeiten({ mail, offen, onClose, onGespeichert }: Props) {
  const toast = useToast();
  const manuellesEvent = mail.event.quelle === 'planung';
  const [versand, setVersand] = useState('');
  const [titel, setTitel] = useState('');
  const [beginn, setBeginn] = useState('');
  const [ende, setEnde] = useState('');
  const [stand, setStand] = useState<PlanungStand>(mail.stand);
  const [feedbackBis, setFeedbackBis] = useState('');
  const [notiz, setNotiz] = useState('');
  const [ausgeblendet, setAusgeblendet] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  // Events aus dem Event Manager; null = (noch) nicht geladen oder Datenbank ohne 0061.
  const [events, setEvents] = useState<EventAuswahl[] | null>(null);
  const [emEvent, setEmEvent] = useState('');

  // Nur beim Öffnen füllen: Neuladen im Hintergrund darf Eingaben nicht überschreiben.
  // Vorbelegt wird, was eingetragen ist (planung) — nicht das Abgeleitete, sonst ginge
  // z. B. ein Versandtermin verloren, solange die Mail in Brevo eingeplant ist.
  useEffect(() => {
    if (!offen) return;
    const pl = mail.planung !== undefined ? mail.planung : {
      // ältere Datenbank ohne planung: wie bisher aus dem Abgeleiteten
      versand_geplant: mail.versand.art === 'geplant' ? mail.versand.am : null,
      event_titel: manuellesEvent ? mail.event.titel : null,
      event_beginn: manuellesEvent ? mail.event.beginn : null,
      event_ende: manuellesEvent ? mail.event.ende : null,
    };
    setVersand(alsEingabe(pl?.versand_geplant));
    setTitel(pl?.event_titel ?? '');
    setBeginn(pl?.event_beginn?.slice(0, 10) ?? '');
    setEnde(pl?.event_ende?.slice(0, 10) ?? '');
    setStand(mail.stand);
    setFeedbackBis(mail.feedback_bis ?? '');
    setNotiz(mail.notiz ?? '');
    setAusgeblendet(mail.ausgeblendet);
    setEvents(null);
    setEmEvent('');
    let aktuell = true;
    ladeEventAuswahl(mail.id)
      .then((liste) => {
        if (!aktuell) return;
        setEvents(liste);
        setEmEvent(liste.find((ev) => ev.zugeordnet)?.id ?? '');
      })
      .catch(() => { /* ohne Auswahl bleibt der Dialog, wie er war */ });
    return () => { aktuell = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen, mail.id]);

  const inBrevo = mail.versand.art === 'eingeplant' || mail.versand.art === 'angehalten' || mail.versand.art === 'versendet';
  const abgeleitet = !manuellesEvent && mail.event.beginn;
  const endeVorBeginn = !!beginn && !!ende && ende < beginn;
  const emVorher = events?.find((ev) => ev.zugeordnet)?.id ?? '';
  const emGewaehlt = events?.find((ev) => ev.id === emEvent) ?? null;

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (endeVorBeginn) return;
    setSpeichert(true);
    try {
      if (events !== null && emEvent !== emVorher) await eventZuordnen(mail.id, emEvent || null);
      await planungSpeichern(mail.id, {
        versand_geplant: versand ? new Date(versand).toISOString() : null,
        event_titel: titel.trim() || null,
        event_beginn: beginn || null,
        event_ende: beginn && ende ? ende : null,
        stand,
        feedback_bis: feedbackBis || null,
        notiz: notiz.trim() || null,
        ausgeblendet,
      });
      toast.success('Gespeichert');
      onGespeichert();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <Modal isOpen={offen} onClose={onClose} title="Termine und Stand" size="md">
      <form className="gd-form" onSubmit={speichern}>
        <p className="gd-betreff">{mail.betreff || mail.name}</p>

        <fieldset className="gd-feld">
          <legend><Send size={15} aria-hidden="true" /> Versand</legend>
          {inBrevo && mail.versand.art && (
            <p className="gd-hinweis">
              {VERSAND_INFO[mail.versand.art].label}: <strong>{datumZeit(mail.versand.am)}</strong>.
              {mail.versand.art !== 'versendet' && ' Den Termin dort änderst Du in Brevo; das Mailtool liest ihn alle 15 Minuten.'}
            </p>
          )}
          <label className="form-label" htmlFor="gd-versand">Geplanter Versand</label>
          <input id="gd-versand" className="form-input" type="datetime-local" value={versand} onChange={(e) => setVersand(e.target.value)} />
          <p className="form-hint">Für Entwürfe, die noch nicht in Brevo eingeplant sind.{!versand && mail.versand.art && !inBrevo && mail.versand.art !== 'geplant' ? ` Ohne Eintrag gilt: ${datumZeit(mail.versand.am)} (${VERSAND_INFO[mail.versand.art].label.toLowerCase()}).` : ''}</p>
        </fieldset>

        <fieldset className="gd-feld">
          <legend><CalendarDays size={15} aria-hidden="true" /> Event</legend>
          {events !== null && (
            <>
              <label className="form-label" htmlFor="gd-em-event">Event im Event Manager</label>
              <select id="gd-em-event" className="form-select" value={emEvent} onChange={(e) => setEmEvent(e.target.value)}>
                <option value="">Keinem Event zugeordnet</option>
                {events.map((ev) => (
                  <option key={ev.id} value={ev.id}>
                    {ev.titel} · {zeitraumText(ev.beginn, ev.ende)}{EM_STATUS[ev.status] ? ` (${EM_STATUS[ev.status]})` : ''}
                  </option>
                ))}
              </select>
              <p className="form-hint">
                {emGewaehlt ? (
                  <>
                    Die Mail steht dann im Event Manager im Reiter „E-Mail“ mit Stand und Link.{' '}
                    <a href={emGewaehlt.link} target="_blank" rel="noreferrer" className="gd-em-link">
                      Im Event Manager öffnen <ExternalLink size={12} aria-hidden="true" />
                    </a>
                    {ausgeblendet && <> Ausgeblendete Mails zeigt der Event Manager nicht.</>}
                  </>
                ) : (
                  'Mit Event erscheint die Mail im Event Manager beim Event — mit Stand und Link hierher.'
                )}
              </p>
            </>
          )}
          {abgeleitet && (
            <p className="gd-hinweis">
              Erkannt {QUELLE_TEXT[mail.event.quelle ?? ''] ?? ''}: <strong>{zeitraumText(mail.event.beginn, mail.event.ende)}</strong>
              {mail.event.titel ? ` · ${mail.event.titel}` : ''}. Nur eintragen, wenn das nicht stimmt.
            </p>
          )}
          <label className="form-label" htmlFor="gd-titel">Name des Events</label>
          <input id="gd-titel" className="form-input" value={titel} maxLength={120} placeholder={mail.event.titel ?? 'z. B. Herbstcamp Spandau'} onChange={(e) => setTitel(e.target.value)} />
          <div className="grid-2 gd-reihe">
            <div>
              <label className="form-label" htmlFor="gd-beginn">Beginn</label>
              <input id="gd-beginn" className="form-input" type="date" value={beginn} onChange={(e) => setBeginn(e.target.value)} />
            </div>
            <div>
              <label className="form-label" htmlFor="gd-ende">Ende (bei mehreren Tagen)</label>
              <input id="gd-ende" className="form-input" type="date" value={ende} min={beginn || undefined} disabled={!beginn} onChange={(e) => setEnde(e.target.value)} />
            </div>
          </div>
          {endeVorBeginn && <p className="gd-fehler" role="alert">Das Ende liegt vor dem Beginn.</p>}
        </fieldset>

        <fieldset className="gd-feld">
          <legend>Abstimmung</legend>
          <div className="grid-2 gd-reihe">
            <div>
              <label className="form-label" htmlFor="gd-stand">Stand</label>
              <select id="gd-stand" className="form-select" value={stand} onChange={(e) => setStand(e.target.value as PlanungStand)}>
                {STAENDE.map((s) => <option key={s} value={s}>{STAND_INFO[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label" htmlFor="gd-bis">Feedback bis</label>
              <input id="gd-bis" className="form-input" type="date" value={feedbackBis} onChange={(e) => setFeedbackBis(e.target.value)} />
            </div>
          </div>
          <p className="form-hint">{STAND_INFO[stand].text} Der Stand wechselt auch von selbst, wenn Rückmeldungen kommen.</p>
          <label className="form-label" htmlFor="gd-notiz">Interne Notiz</label>
          <textarea id="gd-notiz" className="form-textarea" rows={3} maxLength={2000} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder="Nur fürs Team sichtbar — Ansprechpersonen sehen sie nicht." />
          <label className="checkbox-row gd-check">
            <input type="checkbox" checked={ausgeblendet} onChange={(e) => setAusgeblendet(e.target.checked)} />
            <span>In der Übersicht ausblenden (z. B. Testmail)</span>
          </label>
        </fieldset>

        <div className="form-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn-primary" disabled={speichert || endeVorBeginn}>{speichert ? 'Speichert …' : 'Speichern'}</button>
        </div>
      </form>
    </Modal>
  );
}
